import React from "react";
import { useTranslation } from "react-i18next";
import { useMutation, useQuery } from "@apollo/client";
import { Alert, Switch } from "@mui/material";
import { toast } from "@outpave/ds";
import { GET_COMPANY_REWARDS, SET_COMPANY_REWARDS } from "graphql/company";
import CustomSkeleton from "components/common/CustomSkeleton";
import { SettingsCard } from "components/platform/accounts/SettingsCard";
import { getUserFacingErrorMessage } from "utils/graphql";

/** Whether one company earns rewards, on its account's Rewards tab. */
const CompanyRewardsSettings: React.FC<{ companyId: string }> = ({
  companyId,
}) => {
  const { t } = useTranslation();
  const { data, error } = useQuery<{
    company: {
      id: string;
      settings: { REWARDS_ENABLE?: boolean | null } | null;
    } | null;
  }>(GET_COMPANY_REWARDS, {
    variables: { id: companyId },
    fetchPolicy: "network-only",
  });
  const [save, { loading: saving }] = useMutation(SET_COMPANY_REWARDS, {
    onCompleted: () => toast.success(t("AccountRewardsSavedToast")),
    onError: (saveError) =>
      toast.error(
        getUserFacingErrorMessage(
          saveError,
          t("SomethingWentWrongDescriptionWithSupport"),
        ),
      ),
  });

  const company = data?.company;

  return (
    <SettingsCard
      title={t("Rewards")}
      subtitle={t("AccountRewardsHelp")}
      action={
        company ? (
          <Switch
            checked={company.settings?.REWARDS_ENABLE ?? false}
            disabled={saving}
            onChange={(_event, rewards) =>
              void save({ variables: { companyId, rewards } })
            }
            slotProps={{ input: { "aria-label": t("Rewards") } }}
          />
        ) : undefined
      }
    >
      {!company && !error && <CustomSkeleton />}
      {!company && error && (
        <Alert severity="error" role="status">
          {t("AccountRewardsLoadError")}
        </Alert>
      )}
    </SettingsCard>
  );
};

export default CompanyRewardsSettings;
